import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '../stores/authStore'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Keep track of refresh promise to prevent multiple refresh requests
let refreshPromise: Promise<boolean> | null = null

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState()
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    // Let the browser set Content-Type with boundary for FormData uploads
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Don't intercept 401s from auth endpoints — let them propagate to the caller
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/login') ||
                           originalRequest?.url?.includes('/auth/refresh')
    if (error.response?.status === 401 && isAuthEndpoint) {
      return Promise.reject(error)
    }

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        // Use shared refresh promise to prevent multiple refresh requests
        if (!refreshPromise) {
          const { refreshAccessToken } = useAuthStore.getState()
          refreshPromise = refreshAccessToken()
        }

        const refreshed = await refreshPromise
        refreshPromise = null

        if (refreshed) {
          const { accessToken } = useAuthStore.getState()
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`
          }
          return api(originalRequest)
        } else {
          // Refresh failed, redirect to login
          window.location.href = '/login'
          return Promise.reject(error)
        }
      } catch (refreshError) {
        refreshPromise = null
        // Refresh failed, redirect to login
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    // Handle other 401 errors (when _retry is already true)
    if (error.response?.status === 401) {
      const { logout } = useAuthStore.getState()
      await logout()
      window.location.href = '/login'
      return Promise.reject(error)
    }

    return Promise.reject(error)
  }
)

export default api
